import { useLayoutEffect, useRef, useState } from "react";

interface FitScaleInput {
  availableWidth: number;
  availableHeight: number;
  width: number;
  height: number;
  min: number;
  max: number;
}

export function fitScale({
  availableWidth,
  availableHeight,
  width,
  height,
  min,
  max,
}: FitScaleInput): number {
  const raw = Math.min(availableWidth / width, availableHeight / height);
  return Math.min(max, Math.max(min, raw));
}

interface UsePhoneFitScaleOptions {
  width: number;
  height: number;
  min?: number;
  max?: number;
  gutter?: number;
}

// Nearest ancestor that clips/scrolls; its visible box bounds the phone.
function scrollParent(el: HTMLElement): HTMLElement {
  let node = el.parentElement;
  while (node) {
    if (getComputedStyle(node).overflowY !== "visible") return node;
    node = node.parentElement;
  }
  return document.documentElement;
}

export function usePhoneFitScale({
  width,
  height,
  min = 0.45,
  max = 1,
  gutter = 24,
}: UsePhoneFitScaleOptions) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(min);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const container = scrollParent(el);

    const measure = (): number | null => {
      if (el.clientWidth === 0) return null;
      const rect = el.getBoundingClientRect();
      const bounds = container.getBoundingClientRect();
      // Space below the stage bounded two ways, whichever is tighter:
      // - byContainer: stage top measured inside the container's content, so
      //   scroll cancels out — keeps a stable size on height-bounded panels.
      // - byViewport: catches containers that aren't height-bounded (they grow
      //   with their content, so clientHeight can't cap the phone).
      // They're equal at rest when the phone fits, so there's no scroll to
      // rescale on; they only diverge once a panel actually overflows.
      const offsetTop = rect.top - bounds.top + container.scrollTop;
      const byContainer = container.clientHeight - offsetTop;
      const byViewport = Math.min(window.innerHeight, bounds.bottom) - rect.top;
      const availableHeight = Math.min(byContainer, byViewport) - gutter;
      const next = fitScale({
        availableWidth: el.clientWidth,
        availableHeight,
        width,
        height,
        min,
        max,
      });
      setScale(next);
      return next;
    };

    // Re-measure across frames until the scale settles. A panel that slides in
    // via transform (framer-motion drawer) moves without changing its own size,
    // so ResizeObserver/transitionend never fire — without this the phone locks
    // in the scale it had while still off-screen mid-animation.
    // ponytail: rAF-until-stable, capped at ~1s of frames as a backstop.
    let raf = 0;
    let last = -1;
    let frames = 0;
    const settle = () => {
      const next = measure();
      if (next !== null && next !== last && frames++ < 60) {
        last = next;
        raf = requestAnimationFrame(settle);
      }
    };
    const restart = () => {
      cancelAnimationFrame(raf);
      last = -1;
      frames = 0;
      settle();
    };

    restart();
    // ResizeObserver fires on real size changes only (panel resize, drawer
    // open) — never on scroll — so the phone can't grow as you scroll.
    const resize = new ResizeObserver(restart);
    resize.observe(el);
    resize.observe(container);
    window.addEventListener("resize", restart);
    return () => {
      cancelAnimationFrame(raf);
      resize.disconnect();
      window.removeEventListener("resize", restart);
    };
  }, [width, height, min, max, gutter]);

  return { ref, scale };
}
